import React from "react";
import { Box, Typography } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import i18next from "i18next";
import { BRAND, DOSSIER_STATUS, hexToRgba } from "../lightTheme";
import { formatDt } from "../utils/lightUtils";

/**
 * A single document row for Light mode lists.
 * @param {object} doc - document object (documentName, statusCode, depositDate, canOpen, acquisition, etc.)
 * @param {function} onView - called when clicking the row or "see" button
 * @param {function} [onRespond] - called when clicking the respond/update button (receives doc + event)
 * @param {boolean} [compact=false] - smaller icons (28px) for accordion view
 * @param {boolean} [showSeeAlways=true] - always show "see" button even if !canOpen
 * @param {object} classes - { docRow, docName, docMeta, docStatusChip, docActionBtn } from parent useStyles
 */
export default function DocumentRow({ doc, onView, onRespond, compact = false, showSeeAlways = true, classes: s }) {
  const docCfg = DOSSIER_STATUS[doc.statusCode] ?? DOSSIER_STATUS.INCONNU;
  const iconSize = compact ? "28px" : "32px";
  const iconRadius = compact ? "6px" : "8px";
  const iconFontSize = compact ? "14px" : "16px";
  const iconBg = compact ? BRAND.background : `${docCfg.color}15`;
  const iconColor = compact ? BRAND.primary : docCfg.color;
  const showSee = showSeeAlways || doc.canOpen;

  return (
    <Box
      key={doc.documentCode}
      className={s.docRow}
      style={doc.canOpen ? { cursor: "pointer" } : { cursor: "default", opacity: compact ? 0.7 : 0.6 }}
      onClick={() => { if (doc.canOpen || !compact) onView(doc); }}
    >
      <Box style={{ width: iconSize, height: iconSize, borderRadius: iconRadius, backgroundColor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <ArticleOutlinedIcon style={{ fontSize: iconFontSize, color: iconColor }} />
      </Box>
      <Typography className={s.docName}>{doc.documentName}</Typography>
      {doc.depositDate && <Typography className={s.docMeta}>{formatDt(doc.depositDate)}</Typography>}
      <Box className={s.docStatusChip} style={{ backgroundColor: hexToRgba(docCfg.color, 0.18), color: docCfg.color }}>{docCfg.label}</Box>
      {showSee && (
        <Box component="span" className={s.docActionBtn}>
          <VisibilityOutlinedIcon style={{ fontSize: "13px" }} /> {i18next.t("light.common.see")}
        </Box>
      )}
      {(doc.acquisition === "DO" || doc.acquisition === "DO_FO") && onRespond && (
        <Box
          component="span"
          className={s.docActionBtn}
          style={{ opacity: 1 }}
          onClick={(e) => { e.stopPropagation(); onRespond(doc, e); }}
        >
          <ReplyOutlinedIcon style={{ fontSize: "13px", transform: "scaleX(-1)" }} />
          {["IN_PROGRESS", "MISSING"].includes(doc.statusCode) ? i18next.t("light.common.respond") : i18next.t("light.common.updateResponse")}
        </Box>
      )}
    </Box>
  );
}
